import React from "react";
import { useLocation } from "react-router-dom";
// Components
import SEO from "components/CBSEO";
import SchemaComponent from "components/CBSchema";
import CBDivider from "components/CBDivider";
import Page404 from "pages/404";
import TextBlock from "components/TextBlock";
import DynamicHero from "components/DynamicHero";
import { TwoColumnSection } from "styles/components/twoColumnSection";
import ThreeColumnSection from "styles/components/threeColumnSection";
import { ImageContainer } from "styles/components/imageContainer";
import FAQContent from "components/CBFAQ";
import Testimonials from "components/Testimonials";
import HowItWorks from "components/CBHowItWorks";

// Styled Components
import { StyledPageContainer } from "styles/components/PageContainer/index.styles";
// Data
import ketamineStateContent from "./data/pageContentData/ketamineStateContent.data.json";
import psilocybinStateContent from "./data/pageContentData/psilocybinStateContent.data.json";
import ketaminStateSchemaData from "./data/schemaData/ketamineStateSchema.data.json";
import psilocybinStateSchemaData from "./data/schemaData/psilocybinStateSchema.data.json";
import couplesTherapyStateContent from "./data/pageContentData/couplesTherapyStateContent.data.json";
import individualTherapyStateContent from "./data/pageContentData/individualTherapyStateContent.data.json";
import veteranTherapyStateContent from "./data/pageContentData/veteransTherapyStateContent.data.json";
import lgbtqiaTherapyStateContent from "./data/pageContentData/lgbtqiaTherapyStateContent.data.json";
import veteranTherapyStateSchemaData from "./data/schemaData/veteransTherapyStateSchema.data.json";
import couplesTherapyStateSchemaData from "./data/schemaData/couplesTherapyStateSchema.data.json";
import individualTherapyStateSchemaData from "./data/schemaData/individualTherapyStateSchema.data.json";
import lgbtqiaTherapyStateSchemaData from "./data/schemaData/lgbtqiaTherapyStateSchema.data.json";

// Utils
import { capitalizeFirstLetterOfEachWord } from "utils/helpers/capitalizeFirstLetterOfEachWord";
import { constructSchemaData } from "utils/helpers/constructSchemaData";

// Hook
import useDynamicPageRenderer from "utils/hooks/useDynamicPageRenderer";
import InsuranceSection from "components/InsuranceSection";
import StartYourJourney from "pages/TherapyHub/components/startYourJourney";
import FourColumnSection from "styles/components/fourColumnSection";
import GetStartedToday from "pages/TherapyHub/components/GetStartedToday";
import TherapyOptions from "components/TherapyOptions";

const therapyStateContentMap = {
  "ketamine-therapy": ketamineStateContent,
  "psilocybin-therapy": psilocybinStateContent,
  "online-therapy/couples-therapy": couplesTherapyStateContent,
  "online-therapy/individual-therapy": individualTherapyStateContent,
  "online-therapy/veterans": veteranTherapyStateContent,
  "online-therapy/lgbtqia": lgbtqiaTherapyStateContent,
};

const therapyStateSchemaDataMap = {
  "ketamine-therapy": ketaminStateSchemaData,
  "psilocybin-therapy": psilocybinStateSchemaData,
  "online-therapy/couples-therapy": couplesTherapyStateSchemaData,
  "online-therapy/individual-therapy": individualTherapyStateSchemaData,
  "online-therapy/veterans": veteranTherapyStateSchemaData,
  "online-therapy/lgbtqia": lgbtqiaTherapyStateSchemaData,
};

// Components that expect props to be spread directly
const SPREAD_PROPS_COMPONENTS = ["DynamicHero", "TextBlock", "ThreeColumnSection", "Testimonials", "HowItWorks", "TwoColumnSection"];

const componentMap = {
  "GetStartedToday": GetStartedToday,
  "StartYourJourney": StartYourJourney,
  "InsuranceSection": InsuranceSection,
  "TextBlock": TextBlock,
  "DynamicHero": DynamicHero,
  "Divider": CBDivider,
  "TwoColumnSection": TwoColumnSection,
  "ThreeColumnSection": ThreeColumnSection,
  "ImageContainer": ImageContainer,
  "Faq": FAQContent,
  "Testimonials": Testimonials,
  "HowItWorks": HowItWorks,
  "FourColumnSection": FourColumnSection,
  "TherapyOptions": TherapyOptions,
};

const TherapyState = () => {
  const location = useLocation();
  const modalityType = location.pathname.split("/")[1];
  let subModalityType = null;
  if (modalityType === "online-therapy") {
    subModalityType = location.pathname.split("/")[2];
  }

  const contentKey = subModalityType ? `${modalityType}/${subModalityType}` : modalityType;
  const content = therapyStateContentMap[contentKey];
  const contentState = content.find((stateObj) => location.pathname.includes(stateObj.state.toLowerCase()));

  const contentLayout = contentState?.contentLayout;
  const renderedComponents = useDynamicPageRenderer(contentLayout, componentMap, SPREAD_PROPS_COMPONENTS);

  const capitalizedState = capitalizeFirstLetterOfEachWord(contentState.state);
  const therapyType = modalityType === "ketamine-therapy" ? "Ketamine" : modalityType === "psilocybin-therapy" ? "Psilocybin" : "Online";

  const formattedSubModality = subModalityType
    ? subModalityType.includes("-therapy")
      ? subModalityType.split("-")[0].charAt(0).toUpperCase() + subModalityType.split("-")[0].slice(1)
      : subModalityType.charAt(0).toUpperCase() + subModalityType.slice(1)
    : "";

  const dynamicBreadcrumbName =
    modalityType === "online-therapy"
      ? `${therapyType} ${formattedSubModality} Therapy in ${capitalizedState}`
      : `${therapyType} Therapy in ${capitalizedState}`;
  const dynamicBreadcrumbUrl =
    modalityType === "online-therapy"
      ? `/${modalityType}/${subModalityType}/${contentState.state.toLowerCase()}`
      : `/${modalityType}/${contentState.state.toLowerCase()}`;
  const dynamicBreadcrumb = { name: dynamicBreadcrumbName, url: dynamicBreadcrumbUrl };

  const schemaUrl =
    modalityType === "online-therapy"
      ? `https://www.almadelic.com/${modalityType}/${subModalityType}/${contentState.state.toLowerCase()}`
      : `https://www.almadelic.com/${modalityType}/${contentState.state.toLowerCase()}`;

  const schemaData = constructSchemaData(schemaUrl, [...therapyStateSchemaDataMap[contentKey].breadcrumbs, dynamicBreadcrumb]);

  // Extract FAQ data from contentLayout for FAQPage schema markup
  const faqSection = contentLayout?.find((item) => item.section?.componentType === "Faq");
  if (faqSection?.section?.content?.faqSections) {
    const allFaqs = faqSection.section.content.faqSections.flatMap((section) =>
      section.FAQs.map((faq) => ({ question: faq.question, answer: faq.answer }))
    );
    if (allFaqs.length > 0) {
      schemaData.push({ type: "FAQPage", data: { questions: allFaqs } });
    }
  }

  if (!contentState || contentState.statePage === "not found") return <Page404 />;

  return (
    <StyledPageContainer id={`${modalityType}-therapy-${contentState.state}-page-container`}>
      <SEO title={contentState.title} description={contentState.description} canonicalUrl={contentState.statePage} />
      <SchemaComponent schemas={schemaData} />
      {renderedComponents}
    </StyledPageContainer>
  );
};

export default TherapyState;
